import { Icon } from "semantic-ui-react";

import { useSelectedDealer, useSelectedLocation } from "components";
import { ACSES_COMMUNICATION_EVENT_TYPES, SMS_GATEWAYS } from "models";
import { useCommunicationSettingsQuery } from "modules/CommunicationTemplates/hooks";
import {
  isDiagnoseOverviewAnswered,
  isDiagnoseOverviewSentResentAnswered,
  isKeylockerDropPinOrPickupPinSent,
  isOnlineCheckInAnswered,
  isOnlineCheckInSentAnswered,
  isRepairOrDiagnoseOverviewSentResent,
  isRepairOverviewSentResent,
  isSms
} from "modules/CommunicationTemplates/util";

import type { COMMUNICATION_CHANNELS, COMMUNICATION_EVENTS, KEYLOCKER_COMMUNICATION_EVENT_TYPE } from "models";

type GetCommunicationType = {
  event_type: COMMUNICATION_EVENTS | null;
  keylocker_communication_event_type: KEYLOCKER_COMMUNICATION_EVENT_TYPE | null;
  acses_communication_event_type: ACSES_COMMUNICATION_EVENT_TYPES | null;
};

interface GetCommunicationStatusInterface extends GetCommunicationType {
  channel: COMMUNICATION_CHANNELS;
}

const [activeStatusIcon, inactiveStatusIcon] = [
  <Icon name="eye" key="active-status-eye" color="green" />,
  <Icon name="eye slash" key="inactive-status-eye-slash" color="grey" />
];

export const CommunicationStatusIcon = ({ acses_communication_event_type, channel, event_type, keylocker_communication_event_type }: GetCommunicationStatusInterface) => {
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const { data: communicationSettings } = useCommunicationSettingsQuery();

  const isKeylockerEnabled = selectedLocation?.is_keylocker_enabled;
  const isOnlineCheckInEnabled = selectedLocation?.online_check_in_enabled;
  const isRepairOverviewEnabled = selectedLocation?.repair_overview_enabled;
  const isDiagnoseOverviewEnabled = selectedLocation?.diagnose_overview_enabled;
  const isSendConfirmationEmailsEnabled = communicationSettings?.send_confirmation_emails;
  const isSmsForOnlineCheckInEnabled = communicationSettings?.is_sms_for_automated_customer_communication_enabled;
  const isThirdPartySendCommunicationsEnabled = selectedLocation?.third_party_send_communications;
  const isSmsGatewayDisabled = selectedDealer?.sms_gateway_id === SMS_GATEWAYS.Disabled;
  const isDropBackPinSent = acses_communication_event_type === ACSES_COMMUNICATION_EVENT_TYPES.DropBackPinSent;

  if (isThirdPartySendCommunicationsEnabled && (isSms(channel) || !isRepairOrDiagnoseOverviewSentResent(event_type))) {
    return inactiveStatusIcon;
  }

  if (isOnlineCheckInEnabled && isOnlineCheckInSentAnswered(event_type)) {
    if ((isSms(channel) && (!isSmsForOnlineCheckInEnabled || isSmsGatewayDisabled)) || (isOnlineCheckInAnswered(event_type) && !isSendConfirmationEmailsEnabled))
      return inactiveStatusIcon;

    return activeStatusIcon;
  }

  if (isDiagnoseOverviewEnabled && isDiagnoseOverviewSentResentAnswered(event_type)) {
    if ((isDiagnoseOverviewAnswered(event_type) && !isSendConfirmationEmailsEnabled) || (isSms(channel) && isSmsGatewayDisabled)) return inactiveStatusIcon;

    return activeStatusIcon;
  }

  if (isRepairOverviewEnabled && isRepairOverviewSentResent(event_type)) {
    if (isSms(channel) && isSmsGatewayDisabled) return inactiveStatusIcon;

    return activeStatusIcon;
  }

  if (isKeylockerDropPinOrPickupPinSent(keylocker_communication_event_type) && isKeylockerEnabled) {
    return activeStatusIcon;
  }
  if (isDropBackPinSent && selectedLocation?.is_acses_enabled) {
    return activeStatusIcon;
  }

  return inactiveStatusIcon;
};
